/**
 * Activity feed helpers — pure functions for rendering activity events.
 *
 * Extracted from FloatingTransactionPanel for reuse in ActivityRail.
 * No React dependency — safe for lib-level imports.
 */

import type { ActivityEvent, ActivityEventType } from '@/contexts/ActivityContext';

export type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

/** Derive connection status by walking events newest-first. */
export function deriveConnectionStatus(events: ActivityEvent[]): ConnectionStatus {
  for (const e of events) {
    if (e.type === 'connection_restored') return 'connected';
    if (e.type === 'connection_lost') return 'reconnecting';
  }
  return 'connected';
}

/** Format a timestamp as "3:04:05 PM". */
export function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

export const statusDot: Record<ConnectionStatus, string> = {
  connected: 'bg-green-500',
  reconnecting: 'bg-yellow-500 animate-pulse',
  disconnected: 'bg-red-500',
};

export const statusLabel: Record<ConnectionStatus, string> = {
  connected: 'Connected',
  reconnecting: 'Reconnecting...',
  disconnected: 'Disconnected',
};

export function eventTypeLabel(type: ActivityEventType): string {
  switch (type) {
    case 'order_filled':
      return 'fill';
    case 'order_placed':
      return 'new';
    case 'order_cancelled':
      return 'cxl';
    case 'connection_lost':
      return 'disc';
    case 'connection_restored':
      return 'conn';
    case 'connection_change':
      return 'conn';
    case 'error':
      return 'err';
    case 'queue_velocity':
      return 'vel';
  }
}

export function eventTypeColor(type: ActivityEventType): string {
  switch (type) {
    case 'order_filled':
      return 'text-emerald-400';
    case 'order_placed':
      return 'text-blue-400';
    case 'order_cancelled':
      return 'text-amber-400';
    case 'connection_lost':
      return 'text-red-400';
    case 'connection_restored':
      return 'text-emerald-400';
    case 'connection_change':
      return 'text-zinc-400';
    case 'error':
      return 'text-red-400';
    case 'queue_velocity':
      return 'text-zinc-500';
  }
}
